"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { cva } from "class-variance-authority";
import { Button } from "@ui/components/core/Button";
import ChevronLeft24 from "@ui/icons/24x24/chevron-left-24.svg";
import ChevronRight24 from "@ui/icons/24x24/chevron-right-24.svg";
import { useTranslations } from "next-intl";
import { useMove } from "react-aria";
import { useHasFinePointer } from "@/app/_utilities/useHasFinePointer";

interface AutoScrollGridProps {
  children: ReactNode[];
  className?: string;
  scrollInterval?: number; // milliseconds between auto-scrolls
  variant?: "three-columns" | "four-columns" | "five-columns"; // decides the behavior on xl+
  mdColumns?: 2 | 3; // number of columns on md breakpoint (default: 3)
  onLeftmostItemChange?: (index: number) => void; // callback when leftmost visible item changes
  scrollToIndex?: number; // programmatically scroll to this index
  initialScrollIndex?: number; // initial scroll position (item index)
  showNavigation?: boolean; // show prev/next navigation buttons
  hideScrollbar?: boolean; // hide the scrollbar
  isPaused?: boolean; // pause auto-scrolling from parent
  withEdgeGaps?: boolean; // add spacing at the beginning and end of the slider (same as inter-card gap)
  alwaysShowNavigation?: boolean; // show navigation buttons always instead of only on hover
}

// Styles for the different variants (applied on desktop only)
// Card widths: N columns with (N-1) gaps between them → (100% - (N-1)*1rem) / N
// Note: withEdgeGaps uses container padding to create edge spacing, so card widths stay the same
const itemVariants = cva(
  [
    "tw-flex-none tw-self-stretch",
    // mobile: full width with 2rem "peek"
    "tw-w-[calc(100%-2rem)] tw-max-w-80",
    // tablet: 1/2 width with 2rem "peek", and account for gap (0.5rem on sm, 1rem on md+)
    "sm:tw-w-[calc((100%-0.5rem-2rem)*1/2)]",
  ],
  {
    variants: {
      variant: {
        // Note: 1.9 instead of 2 compensates for sub-pixel rounding in 3-column layouts that caused
        // weird clipping issues on the left hand card.
        "three-columns": "xl:tw-w-[calc((100%-1.9*1rem)/3)]",
        "four-columns": "xl:tw-w-[calc((100%-3*1rem)/4)]",
        "five-columns": "xl:tw-w-[calc((100%-4*1rem)/5)]",
      },
      mdColumns: {
        2: "md:tw-w-[calc((100%-1rem)/2)] md:tw-max-w-none",
        3: "md:tw-w-[calc((100%-2*1rem)/3)] md:tw-max-w-none",
      },
      // withEdgeGaps doesn't affect card sizing - edge gaps are created via container padding
      withEdgeGaps: {
        true: "",
        false: "",
      },
    },
    defaultVariants: {
      variant: "five-columns",
      mdColumns: 3,
      withEdgeGaps: false,
    },
  },
);

/**
 * A responsive auto-scrolling layout component that:
 * - On mobile: horizontal scrolling with "peek" effect
 * - On md: 3 columns with calculated widths
 * - On xl:
 *    - three-columns: 3 columns with calculated widths
 *    - four-columns: 4 columns with calculated widths
 *    - five-columns: 5 columns with calculated widths (default)
 * - Automatically scrolls to reveal more items when not actively interacting
 * - With `withEdgeGaps`: adds consistent spacing at the beginning and end of the slider (same as inter-card gap)
 */
export function AutoScrollGrid({
  children,
  className,
  scrollInterval = 4000,
  variant = "five-columns",
  mdColumns = 3,
  onLeftmostItemChange,
  scrollToIndex,
  initialScrollIndex,
  showNavigation = false,
  hideScrollbar = false,
  isPaused = false,
  withEdgeGaps = false,
  alwaysShowNavigation = false,
}: AutoScrollGridProps) {
  const items = Array.isArray(children) ? children : [children];
  const t = useTranslations("ClientKeys.Globals");

  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const firstItemRef = useRef<HTMLLIElement>(null);
  const intervalRef = useRef<number | undefined>(undefined);
  const interactionTimeoutRef = useRef<number | undefined>(undefined);
  const lastCenterIndexRef = useRef<number>(-1);
  const scrollTimeoutRef = useRef<number | undefined>(undefined);
  const isProgrammaticScrollRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const hasFinePointer = useHasFinePointer();

  // Helper to get scroll metrics
  const getScrollMetrics = useCallback(() => {
    const container = scrollContainerRef.current;
    const firstItem = firstItemRef.current;
    if (!container || !firstItem) return null;

    const itemWidth = firstItem.offsetWidth;
    // Read actual computed gap from CSS (responsive: 0.5rem on mobile, 1rem on md+)
    const gap = parseFloat(getComputedStyle(container).gap) || 16;
    const scrollAmount = itemWidth + gap;

    return { container, itemWidth, gap, scrollAmount };
  }, []);

  // Check if we need to show the previous or next button
  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const currentScroll = container.scrollLeft;

    setCanScrollPrev(currentScroll > 10);
    setCanScrollNext(currentScroll < maxScroll - 10);
  }, []);

  const handleAutoScroll = useCallback(() => {
    const metrics = getScrollMetrics();
    if (!metrics) return;

    const { container, scrollAmount } = metrics;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const currentScroll = container.scrollLeft;

    // Check if current position is aligned to an item boundary
    const remainder = currentScroll % scrollAmount;
    const isAligned = remainder < 5 || remainder > scrollAmount - 5; // 5px tolerance

    if (currentScroll >= maxScroll - 10) {
      // At the end, scroll back to start
      container.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    } else if (!isAligned) {
      // Not aligned to item boundary - snap to nearest aligned position
      const nearestAlignedPosition = Math.round(currentScroll / scrollAmount) * scrollAmount;
      container.scrollTo({
        left: nearestAlignedPosition,
        behavior: "smooth",
      });
    } else {
      // Aligned - scroll forward by one item width
      container.scrollTo({
        left: currentScroll + scrollAmount,
        behavior: "smooth",
      });
    }

    updateScrollState();
  }, [getScrollMetrics, updateScrollState]);

  const startAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = window.setInterval(handleAutoScroll, scrollInterval);
  }, [handleAutoScroll, scrollInterval]);

  const stopAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, []);

  const handleUserInteraction = useCallback(() => {
    // Stop auto-scrolling
    stopAutoScroll();

    // Clear existing timeout
    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    // Resume auto-scroll after 5 seconds of no interaction (only if not paused)
    if (!isPaused) {
      interactionTimeoutRef.current = window.setTimeout(() => {
        startAutoScroll();
      }, 5000);
    }
  }, [stopAutoScroll, startAutoScroll, isPaused]);

  // useMove hook for drag-to-scroll
  const { moveProps } = useMove({
    onMoveStart: () => {
      setIsDragging(true);
      handleUserInteraction();
    },
    onMoveEnd: () => {
      setIsDragging(false);
    },
    onMove: e => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Keyboard arrow keys should behave like navigation buttons
      if (e.pointerType === "keyboard") {
        if (e.deltaX > 0) {
          scrollNext();
        } else if (e.deltaX < 0) {
          scrollPrev();
        }
        return;
      }

      container.scrollLeft -= e.deltaX * 1.5; // Scroll speed multiplier
    },
  });

  const scrollPrev = useCallback(() => {
    const metrics = getScrollMetrics();
    if (!metrics) return;

    const { container, scrollAmount } = metrics;
    const currentScroll = container.scrollLeft;

    isProgrammaticScrollRef.current = true;
    container.scrollTo({
      left: Math.max(0, currentScroll - scrollAmount),
      behavior: "smooth",
    });

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      // Force update scroll state after animation completes
      updateScrollState();
      // Also trigger a scroll event to ensure state updates
      container.dispatchEvent(new Event("scroll"));
    }, 600);

    handleUserInteraction();
  }, [getScrollMetrics, updateScrollState, handleUserInteraction]);

  const scrollNext = useCallback(() => {
    const metrics = getScrollMetrics();
    if (!metrics) return;

    const { container, scrollAmount } = metrics;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const currentScroll = container.scrollLeft;

    isProgrammaticScrollRef.current = true;
    container.scrollTo({
      left: Math.min(maxScroll, currentScroll + scrollAmount),
      behavior: "smooth",
    });

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      // Force update scroll state after animation completes
      updateScrollState();
      // Also trigger a scroll event to ensure state updates
      container.dispatchEvent(new Event("scroll"));
    }, 600);

    handleUserInteraction();
  }, [getScrollMetrics, updateScrollState, handleUserInteraction]);

  const getLeftmostItemIndex = useCallback(() => {
    const metrics = getScrollMetrics();
    if (!metrics) return 0;

    const { container, scrollAmount } = metrics;

    // Calculate which item is at the left edge of the viewport
    const currentScroll = container.scrollLeft;
    const leftmostIndex = Math.round(currentScroll / scrollAmount);

    return Math.min(Math.max(0, leftmostIndex), items.length - 1);
  }, [getScrollMetrics, items.length]);

  const scrollToIndexHandler = useCallback(
    (index: number, behavior: ScrollBehavior = "smooth") => {
      const metrics = getScrollMetrics();
      if (!metrics) return;

      const { container, scrollAmount } = metrics;

      isProgrammaticScrollRef.current = true;
      container.scrollTo({
        left: index * scrollAmount,
        behavior,
      });

      // Clear flag and notify after scroll animation completes
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;

        // Notify parent of the new leftmost index after programmatic scroll
        if (onLeftmostItemChange) {
          const leftmostIndex = getLeftmostItemIndex();
          if (leftmostIndex !== lastCenterIndexRef.current) {
            lastCenterIndexRef.current = leftmostIndex;
            onLeftmostItemChange(leftmostIndex);
          }
        }
      }, 500);
    },
    [getScrollMetrics, onLeftmostItemChange, getLeftmostItemIndex],
  );

  // Initial scroll position
  useEffect(() => {
    if (initialScrollIndex !== undefined && !hasInitializedRef.current) {
      scrollToIndexHandler(initialScrollIndex, "auto");
      hasInitializedRef.current = true;
    }
  }, [initialScrollIndex, scrollToIndexHandler]);

  // Handle programmatic scroll
  useEffect(() => {
    if (scrollToIndex !== undefined && scrollToIndex >= 0 && scrollToIndex < items.length) {
      scrollToIndexHandler(scrollToIndex);
      handleUserInteraction(); // Stop auto-scroll temporarily
    }
  }, [scrollToIndex, items.length, scrollToIndexHandler, handleUserInteraction]);

  // Track leftmost item changes and scroll state with debouncing
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Update scroll state for navigation buttons (always, even during programmatic scroll)
      updateScrollState();

      // Ignore leftmost item tracking during programmatic scrolling
      if (isProgrammaticScrollRef.current || !onLeftmostItemChange) return;

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Debounce scroll events
      scrollTimeoutRef.current = window.setTimeout(() => {
        const leftmostIndex = getLeftmostItemIndex();
        if (leftmostIndex !== lastCenterIndexRef.current) {
          lastCenterIndexRef.current = leftmostIndex;
          onLeftmostItemChange(leftmostIndex);
        }
      }, 100);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [getLeftmostItemIndex, onLeftmostItemChange, updateScrollState]);

  // Initialize scroll state on mount
  useEffect(() => {
    updateScrollState();
  }, [updateScrollState]);

  // Initialize auto-scroll and cleanup
  useEffect(() => {
    if (items.length <= 1) return;

    startAutoScroll();

    return () => {
      stopAutoScroll();
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, [items.length, startAutoScroll, stopAutoScroll]);

  // Handle pause/resume from parent
  useEffect(() => {
    if (isPaused) {
      stopAutoScroll();
      // Also clear any pending interaction timeout that would restart scrolling
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
        interactionTimeoutRef.current = undefined;
      }
    } else if (items.length > 1) {
      startAutoScroll();
    }
  }, [isPaused, items.length, startAutoScroll, stopAutoScroll]);

  return (
    <div className={clsx("tw-flow-root tw-group/grid tw-relative", className)}>
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- drag-to-scroll carousel */}
      <ul
        ref={scrollContainerRef}
        role='list'
        tabIndex={0}
        className={clsx(
          "tw-flex tw-items-stretch tw-gap-2 md:tw-gap-4 tw-overflow-x-auto tw-pt-4 -tw-mt-4 tw-pb-5 -tw-mb-5 -tw-mx-full-bleed focus-visible:tw-outline-none",
          // When withEdgeGaps, add extra 0.5rem padding on mobile and 1rem padding on desktop to show gap before first and after last item
          withEdgeGaps
            ? "tw-px-[calc(var(--full-bleed,0px)+0.5rem)] md:tw-px-[calc(var(--full-bleed,0px)+1rem)]"
            : "tw-px-full-bleed",
          // Only show grab cursor on devices with fine pointer
          hasFinePointer && (isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab"),
          hideScrollbar ? "tw-scrollbar-none" : "tw-scrollbar-gray-200 tw-scrollbar-thin",
        )}
        onWheel={handleUserInteraction}
        // Only apply moveProps on devices with fine pointer (desktop)
        {...(hasFinePointer ? moveProps : {})}
      >
        {items.map((child, index) => (
          <li
            key={index}
            ref={index === 0 ? firstItemRef : null}
            className={itemVariants({ variant, mdColumns, withEdgeGaps })}
          >
            {child}
          </li>
        ))}
      </ul>
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}

      {/* Navigation buttons - visible on hover or always (lg+ only), outside container */}
      {showNavigation && (
        <div
          className={clsx(
            "tw-pointer-events-none tw-absolute tw-inset-y-0 -tw-left-[calc(2.75rem+1rem+var(--page-section-px,0px))] -tw-right-[calc(2.75rem+1rem+var(--page-section-px,0px))] tw-hidden tw-items-center tw-justify-between lg:tw-flex",
            !alwaysShowNavigation &&
              "tw-opacity-0 tw-transition-opacity tw-duration-200 group-hover/grid:tw-opacity-100",
          )}
        >
          {/* Button size is 44px (tw-size-11), so use half of that (22px) as left/right offset */}
          <div className='tw-pointer-events-auto'>
            <Button
              aria-label={t("previous_slide")}
              className={clsx(
                "tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity tw-duration-200",
                !canScrollPrev && "tw-opacity-30 tw-cursor-not-allowed",
              )}
              disabled={!canScrollPrev}
              icon={<ChevronLeft24 />}
              onClick={scrollPrev}
              variant='elevated'
            />
          </div>
          <div className='tw-pointer-events-auto'>
            <Button
              aria-label={t("next_slide")}
              className={clsx(
                "tw-flex tw-size-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-p-0 tw-transition-opacity tw-duration-200",
                !canScrollNext && "tw-opacity-30 tw-cursor-not-allowed",
              )}
              disabled={!canScrollNext}
              icon={<ChevronRight24 />}
              onClick={scrollNext}
              variant='elevated'
            />
          </div>
        </div>
      )}
    </div>
  );
}
